import { parseJSON } from 'date-fns';
import React, { Dispatch, FunctionComponent, SetStateAction, useState } from 'react';
import { ImageVersion } from '../../../../server/src/models/environment';
import { formatBytes, percentageWidthStyle } from '../../../utils/format';
import { trpc } from '../../../utils/trpc';
import { ToolTip } from '../../ToolTip';

type WizardStepProps = {
  steps: number;
  step: number;
  stepMessage: string;
  setStep: Dispatch<SetStateAction<number>>;
  setVersionHandler: Dispatch<SetStateAction<ImageVersion | undefined>>;
  repository: string;
};

const WizardStep: FunctionComponent<WizardStepProps> = ({
  steps,
  step,
  stepMessage,
  setStep,
  repository,
  setVersionHandler
}) => {
  const images = trpc.image.findImagesByRepository.useQuery(repository);

  const [state, setState] = useState<Partial<ImageVersion>>({
    repo: repository
  });

  const selectHandler = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const [tag, hash] = target.value.split('|');

    console.log(`selectHandler() tag: ${tag}, `);
    setState({
      ...state,
      hash,
      tag
    });
  };

  const buttonHandler = () => {
    const imageVersion = images?.data?.find(
      (image) => image.imageDigest === state.hash && image.imageTag === state.tag
    );
    if (!imageVersion || !imageVersion.imageDigest || !imageVersion.imageTag) {
      console.log(`Image not found or mismatch: Got ${imageVersion ?? 'nothing'}.`);
      return;
    }

    setVersionHandler({
      hash: imageVersion.imageDigest,
      repo: imageVersion.repositoryName!,
      tag: imageVersion.imageTag
    });
    setStep(step + 1);
  };

  return (
    <div className="basis-11/12 md:basis-7/12 md:px-0 px-4">
      <div className="border-b-2 py-4">
        <div className="uppercase tracking-wide text-xs font-bold text-gray-500 mb-1 leading-tight">
          Step {step} of {steps}
        </div>
        <div className="flex flex-row items-center justify-between py-2">
          <div className="basis-1/2">
            <div className="text-lg font-bold text-gray-700 leading-tight left">{stepMessage}</div>
          </div>

          <div className="basis-1/2 flex-row flex items-center px-3">
            <div className="flex-auto bg-gray-200 rounded-full mr-2">
              <div
                className="rounded-full bg-green-500 text-xs leading-none h-2 text-center text-white"
                style={percentageWidthStyle(step / steps)}
              />
            </div>
            <div className="flex-none text-xs w-10 text-gray-600">
              {Math.round((step / steps) * 100).toFixed(0) + '%'}
            </div>
          </div>
        </div>
      </div>

      <div className="mt4 flex flex-col py-10">
        <div className="max-h-96 relative overflow-x-auto overflow-y-auto shadow-md sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-6 py-3">
                  Repository
                </th>
                <th scope="col" className="px-6 py-3">
                  Version
                </th>
                <th scope="col" className="px-6 py-3">
                  Digest
                </th>
                <th scope="col" className="px-6 py-3">
                  Size
                </th>
                <th scope="col" className="px-6 py-3">
                  Pushed
                </th>
                <th scope="col" className="px-6 py-3">
                  <span className="sr-only">Select</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {!images.data && (
                <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                  <th scope="row" className="px-6 py-4 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                    <svg
                      role="status"
                      className="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  </th>
                  <td className="px-6 py-4">Loading ...</td>
                  <td className="px-6 py-4">&nbsp;</td>
                  <td className="px-6 py-4">&nbsp;</td>
                  <td className="px-6 py-4">&nbsp;</td>
                  <td className="px-6 py-4 text-right">&nbsp;</td>
                </tr>
              )}
              {images.data &&
                images.data.map((image) => (
                  <tr
                    key={`${image.imageTag}|${image.imageDigest}`}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <th scope="row" className="px-6 py-4 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {image.repositoryName}
                    </th>
                    <td className="px-6 py-4">{image.imageTag ?? '<none>'}</td>
                    <td className="px-6 py-4">
                      <ToolTip message={image.imageDigest!}>{image.imageDigest?.substring(0, 15)}...</ToolTip>
                    </td>
                    <td className="px-6 py-4">{formatBytes(image.sizeInBytes!, 0)}</td>
                    <td className="px-6 py-4">{parseJSON(image.imagePushedAt!).toLocaleString()}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center">
                        <input
                          onChange={selectHandler}
                          id="imageRadio"
                          type="radio"
                          value={`${image.imageTag}|${image.imageDigest}`}
                          checked={state.tag === image.imageTag}
                          name="tag"
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-row pt-10 justify-end">
          {state.tag && (
            <button
              type="button"
              onClick={() => buttonHandler()}
              className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
            >
              Select
            </button>
          )}
          {!state.tag && (
            <button
              type="button"
              className="text-white bg-blue-400 dark:bg-blue-500 cursor-not-allowed font-medium rounded-lg text-sm px-5 py-2.5 text-center"
            >
              Select
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
export default WizardStep;
